import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Divider,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useDeckStore } from '../store/deckStore';
import { api, type DirectoryPerson, type Visibility } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { useDepartmentStore } from '../store/departmentStore';
import { paths } from '../lib/router';

export function ShareDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const deckId = useDeckStore((s) => s.deckId);
  const currentVisibility = useDeckStore((s) => s.visibility);
  const currentCategory = useDeckStore((s) => s.category);
  const setSharing = useDeckStore((s) => s.setSharing);
  const isOwner = useDeckStore((s) => s.isOwner);
  const myEmail = useAuthStore((s) => s.user?.email ?? '');
  const [people, setPeople] = useState<DirectoryPerson[]>([]);
  const [editors, setEditors] = useState<DirectoryPerson[]>([]);
  const [savedEditors, setSavedEditors] = useState<string[]>([]);
  const [editorsLoaded, setEditorsLoaded] = useState(false);
  const departments = useDepartmentStore((s) => s.departments);
  const refreshDepartments = useDepartmentStore((s) => s.refresh);
  const [visibility, setVisibility] = useState<Visibility>('private');
  const [category, setCategory] = useState('general');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  const link = deckId ? `${window.location.origin}/#${paths.view(deckId)}` : '';
  const showLink = visibility === 'company' || visibility === 'unlisted';
  const needsFolder = visibility === 'company';

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await setSharing(visibility, needsFolder ? category : currentCategory || 'general');
      if (useDeckStore.getState().saveStatus === 'offline') {
        throw new Error("Couldn't reach the server. Try again.");
      }
      const emails = editors.map((p) => p.email.toLowerCase()).sort();
      const editorsChanged = isOwner && editorsLoaded && deckId && emails.join(',') !== [...savedEditors].sort().join(',');
      if (editorsChanged) {
        await api.setDeckEditors(deckId, emails);
        setSavedEditors(emails);
      }
      setSaved(true);
      // Keep the dialog open when sharing so the link is visible after first publish.
      if (visibility === 'private' && !editorsChanged) onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update sharing');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="share-title"
      slotProps={{
        transition: {
          onEnter: () => {
            void refreshDepartments();
            setVisibility(currentVisibility);
            setCategory(currentCategory);
            setError(null);
            setCopied(false);
            setSaved(false);
            setEditorsLoaded(false);
            if (deckId && isOwner) {
              void Promise.all([api.directory(), api.deckEditors(deckId)])
                .then(([dir, ed]) => {
                  setPeople(dir.people);
                  setEditors(ed.editors.map((e) => ({ email: e.email, name: e.name })));
                  setSavedEditors(ed.editors.map((e) => e.email.toLowerCase()));
                  setEditorsLoaded(true);
                })
                .catch(() => setEditorsLoaded(false));
            }
          },
        },
      }}
    >
      <DialogTitle id="share-title" component="h2" variant="h6">
        Share deck
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {saved && (
            <Alert severity="success">{showLink ? 'Sharing updated. Copy the link below.' : 'Sharing updated.'}</Alert>
          )}
          <RadioGroup
            value={visibility}
            onChange={(e) => {
              setVisibility(e.target.value as Visibility);
              setSaved(false);
            }}
            aria-label="Who can see this deck"
            sx={{ gap: 0.5 }}
          >
            <FormControlLabel
              value="private"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  Only me
                  <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                    Private to your account
                  </Typography>
                </Typography>
              }
            />
            <FormControlLabel
              value="unlisted"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  Anyone with the link
                  <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                    Not listed on Home. Colleagues who sign in and open the link can view, present, download and
                    copy; only you and your co-editors can edit.
                  </Typography>
                </Typography>
              }
            />
            <FormControlLabel
              value="company"
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  Everyone at AST
                  <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                    Listed on the home page and in a department folder.
                  </Typography>
                </Typography>
              }
            />
          </RadioGroup>
          {needsFolder && (
            <TextField
              select
              label="Department folder"
              value={departments.some((d) => d.id === category) ? category : ''}
              onChange={(e) => setCategory(e.target.value)}
              fullWidth
              helperText="Shared decks appear in this department's folder"
            >
              {departments.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {showLink && link && (
            <Box>
              <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                Link for colleagues (they need to sign in)
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField value={link} fullWidth slotProps={{ htmlInput: { readOnly: true, 'aria-label': 'Share link' } }} />
                <Button
                  variant="outlined"
                  color="neutral"
                  startIcon={<ContentCopyOutlinedIcon />}
                  onClick={() => void navigator.clipboard.writeText(link).then(() => setCopied(true))}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </Stack>
            </Box>
          )}
          {isOwner && deckId && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" component="h3" sx={{ mb: 0.25 }}>
                  Co-editors
                </Typography>
                <Typography variant="caption" component="p" sx={{ mb: 1 }}>
                  They can open and edit this deck from My decks, whatever the setting above.
                </Typography>
                <Autocomplete
                  multiple
                  disabled={!editorsLoaded}
                  options={people.filter((p) => p.email.toLowerCase() !== myEmail.toLowerCase())}
                  value={editors}
                  onChange={(_, v) => {
                    setEditors(v.slice(0, 20));
                    setSaved(false);
                  }}
                  isOptionEqualToValue={(a, b) => a.email.toLowerCase() === b.email.toLowerCase()}
                  getOptionLabel={(p) => (p.name && p.name !== p.email ? `${p.name} (${p.email})` : p.email)}
                  filterSelectedOptions
                  renderValue={(value, getItemProps) =>
                    value.map((p, index) => {
                      const { key, ...itemProps } = getItemProps({ index });
                      return <Chip key={key} size="small" label={p.name || p.email} {...itemProps} />;
                    })
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder={editors.length ? '' : 'Add people by name or email'} />
                  )}
                />
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="neutral" onClick={onClose}>
          {saved ? 'Done' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          disabled={busy || !deckId}
          onClick={() => void save()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
